import type { DBSchema, IDBPDatabase } from 'idb'
import { openDB, unwrap } from 'idb'
import type { AiProposalRecord } from '~/models/aiProposal'
import type { AthleteProfileRecord } from '~/models/athleteProfile'
import type { UnmodelledRecord } from '~/models/common'
import type { ConnectionRecord } from '~/models/connection'
import type { DailyCheckRecord } from '~/models/dailyCheck'
import type { DomainGoalRecord } from '~/models/fitnessDomain'
import type { GoalRecord } from '~/models/goal'
import type { MeasurementSessionRecord } from '~/models/measurementSession'
import type { NutritionPlanRecord } from '~/models/nutritionPlan'
import type { PhaseRecord } from '~/models/phase'
import type { ProjectRecord } from '~/models/project'
import type { RoutineAnchorRecord } from '~/models/routineAnchor'
import type { OpinionRecord } from '~/models/rulesOpinion'
import { DB_SCHEMA_VERSION } from '~/models/schemaVersion'
import type { SentinelLiftRecord } from '~/models/sentinelLift'
import type { SettingsRecord } from '~/models/settings'
import type { StrengthEntryRecord } from '~/models/strengthEntry'
import type { TrainingWeekRecord } from '~/models/trainingWeek'
import type { WeeklyReviewRecord } from '~/models/weeklyReview'
import type { WeightEntryRecord } from '~/models/weightEntry'
import { MIGRATIONS, type Migration, runMigrations } from './migrations'

export const DATABASE_NAME = 'project-sola'

/** Value stored in the operational `meta` store. */
export interface MetaRecord {
  readonly key: string
  readonly value: unknown
}

/**
 * Typed view of the schema.
 *
 * Stores whose entity is modelled in a later checkpoint use `UnmodelledRecord`.
 * They still exist from version 1 because changing store structure later is the
 * expensive operation (D-027).
 */
export interface SolaDB extends DBSchema {
  profile: { key: string; value: AthleteProfileRecord }
  settings: { key: string; value: SettingsRecord }
  projects: { key: string; value: ProjectRecord; indexes: { status: string } }
  goals: {
    key: string
    value: GoalRecord
    indexes: { status: string; targetDate: string; projectId: string }
  }
  phases: {
    key: string
    value: PhaseRecord
    indexes: { startDate: string; status: string; type: string; projectId: string }
  }
  weightEntries: { key: string; value: WeightEntryRecord; indexes: { phaseId: string } }
  /* Typed rather than `UnmodelledRecord` from Checkpoint 2.5, when Project
     Setup began writing a session inside the same transaction as the profile,
     project, phase and goal. The store and its indexes are unchanged; only the
     TypeScript view of them moved, exactly as Checkpoint 2.0 did for the four
     weekly-evidence stores (D-072). */
  measurementSessions: {
    key: string
    value: MeasurementSessionRecord
    indexes: { date: string; reviewId: string; phaseId: string }
  }
  photos: {
    key: string
    value: UnmodelledRecord
    indexes: { capturedDate: string; view: string; reviewId: string; phaseId: string }
  }
  sentinelLifts: {
    key: string
    value: SentinelLiftRecord
    indexes: { active: string; order: number }
  }
  strengthEntries: {
    key: string
    value: StrengthEntryRecord
    indexes: { liftId: string; date: string; liftId_date: [string, string] }
  }
  weeklyReviews: {
    key: string
    value: WeeklyReviewRecord
    indexes: { weekStartDate: string; status: string; phaseId: string; projectId: string }
  }
  opinions: {
    key: string
    value: OpinionRecord
    indexes: { reviewId: string; generatedAt: string }
  }
  attachments: {
    key: string
    value: UnmodelledRecord
    indexes: { kind: string; relatedId: string }
  }
  blobs: { key: string; value: { blobId: string; blob: Blob; mimeType: string; byteSize: number } }
  meta: { key: string; value: MetaRecord }
  /* Version 2, schema v3 (D-090, D-092). The three modelled at C6.0 are typed;
     the four below them use `UnmodelledRecord` until their own checkpoints, the
     same way the v1 schema carried the weekly-evidence stores until D-072. */
  domainGoals: {
    key: string
    value: DomainGoalRecord
    indexes: { projectId: string; domain: string; status: string }
  }
  nutritionPlans: {
    key: string
    value: NutritionPlanRecord
    indexes: { projectId: string; effectiveFrom: string }
  }
  dailyChecks: { key: string; value: DailyCheckRecord; indexes: { projectId: string } }
  /* `enabled` is a string, not a boolean: IndexedDB has no boolean key type and
     silently omits such a record from the index (D-072). */
  routineAnchors: {
    key: string
    value: RoutineAnchorRecord
    indexes: { kind: string; enabled: string }
  }
  wearableImports: {
    key: string
    value: UnmodelledRecord
    indexes: { date: string; category: string; date_category: [string, string] }
  }
  /* Modelled at C7.0 phase 4 (D-094 §5). `requestedAt` is the only index and the
     only lookup: a proposal is immutable once written, so there is nothing to
     find it by other than when it was asked for. Adding a second index means
     changing the schema and its rationale together, not scanning quietly. */
  aiProposals: { key: string; value: AiProposalRecord; indexes: { requestedAt: string } }
  /* Modelled at C7.0 (D-095 §1). Keyed by provider, and **the only store
     excluded from a backup**, because it is the only one that holds a
     credential — enforced in `exports/backup/createBackup.ts`, which until C7.0
     was a claim this comment made and no code kept. */
  connections: { key: string; value: ConnectionRecord }
  /* Modelled at C7.5 (D-105 §6). Keyed by an opaque id following weeklyReviews,
     never by the week start date: a mid-project weekStartDay change and a
     project started mid-week each produce two records claiming the same day. */
  trainingWeeks: {
    key: string
    value: TrainingWeekRecord
    indexes: { weekStartDate: string; projectId: string }
  }
}

export type SolaDatabase = IDBPDatabase<SolaDB>

export interface OpenOptions {
  readonly name?: string
  readonly version?: number
  /** Injectable so tests can supply a deliberately throwing migration. */
  readonly migrations?: readonly Migration[]
  readonly onBlocked?: () => void
  readonly onBlocking?: () => void
}

/**
 * Open the database at a specific version, running migrations if needed.
 *
 * The `upgrade` callback is synchronous by construction: `runMigrations` and
 * every `Migration.run` return `void`, not a promise. That makes it impossible
 * to await non-IndexedDB work inside the `versionchange` transaction, which
 * would otherwise auto-commit it early (D-028).
 */
export async function openDatabase(options: OpenOptions = {}): Promise<SolaDatabase> {
  const name = options.name ?? DATABASE_NAME
  const version = options.version ?? DB_SCHEMA_VERSION
  const migrations = options.migrations ?? MIGRATIONS

  return openDB<SolaDB>(name, version, {
    upgrade(database, oldVersion, newVersion, transaction) {
      // When a migration throws, IndexedDB aborts this versionchange
      // transaction and its `done` promise rejects. We surface that failure
      // through the rejection of `openDB` itself, so mark `done` as handled —
      // otherwise the same failure also escapes as an unhandledrejection event.
      void transaction.done.catch(() => undefined)

      runMigrations({
        // Native handles, not the `idb` wrappers. A migration must issue raw
        // requests and continue in their `onsuccess` callbacks; see the note on
        // `MigrationContext`.
        database: unwrap(database),
        transaction: unwrap(transaction),
        oldVersion,
        newVersion: newVersion ?? version,
        migrations,
      })
    },
    blocked() {
      options.onBlocked?.()
    },
    blocking() {
      // Another tab wants to upgrade. Close this connection so it is not
      // blocked indefinitely (D-028).
      options.onBlocking?.()
    },
  })
}

/**
 * Open whatever version currently exists, without triggering an upgrade.
 *
 * Used by the boot preflight and by recovery mode. Callers must first confirm
 * the database exists — opening a non-existent database with no version creates
 * an empty one at version 1, which would then look like a completed migration.
 */
export async function openExistingDatabase(name: string = DATABASE_NAME): Promise<IDBPDatabase> {
  return openDB(name)
}

/** Whether a database of this name already exists in the origin. */
export async function databaseExists(name: string = DATABASE_NAME): Promise<boolean | 'unknown'> {
  if (typeof indexedDB === 'undefined') return false
  if (typeof indexedDB.databases !== 'function') return 'unknown'
  try {
    const databases = await indexedDB.databases()
    return databases.some((entry) => entry.name === name)
  } catch {
    return 'unknown'
  }
}

export function isIndexedDbAvailable(): boolean {
  return typeof indexedDB !== 'undefined' && indexedDB !== null
}
